// GeoPolygonFacet — platform facet for area-of-interest (AOI) polygon filters.
//
// Shape: { polygons: Array<{ id, label?, polygon: GeoJSON.Polygon }> }
// One field can hold many AOIs (Shift-click multi-AOI on the map).
//
// This facet is non-interactive on its own — drawing happens on whichever
// surface owns the map. The "edit" button calls `onOpenEditor` if provided
// or, as a Phase A bridge, dispatches a window-level `intel.openAoiTool`
// event the consumer page can listen for. Real terra-draw wiring lands in
// Phase C.

import { FilterChip } from '../FilterChip.js';

export interface GeoPolygonEntry {
  id: string;
  label?: string;
  polygon: {
    type: 'Polygon';
    coordinates: number[][][]; // [ ring ][ position ][ lng, lat (, alt) ]
  };
}

export interface GeoPolygonFacetValue {
  polygons: GeoPolygonEntry[];
}

export interface GeoPolygonFacetProps {
  field: string;
  label: string;
  value: GeoPolygonFacetValue;
  onChange: (next: GeoPolygonFacetValue) => void;
  /** Called when the user clicks the "edit AOI" button. Falls back to
   *  dispatching `intel.openAoiTool` on `window` so legacy hosts can hook in
   *  without prop drilling. */
  onOpenEditor?: () => void;
}

export function GeoPolygonFacet({
  field,
  label,
  value,
  onChange,
  onOpenEditor,
}: GeoPolygonFacetProps) {
  const polygons = value?.polygons ?? [];

  const remove = (id: string) => onChange({ polygons: polygons.filter((p) => p.id !== id) });

  const openEditor = () => {
    if (onOpenEditor) {
      onOpenEditor();
      return;
    }
    // Phase A bridge — Phase C swaps in real terra-draw wiring via a context
    // provider rather than a window event.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('intel.openAoiTool', { detail: { field } }));
    }
  };

  return (
    <fieldset
      data-facet={field}
      style={{
        border: 'none',
        borderTop: '1px solid var(--bd)',
        margin: 0,
        padding: '8px 10px 10px',
      }}
    >
      <legend
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        <span>{label}</span>
        {polygons.length > 0 && (
          <button
            type="button"
            onClick={() => onChange({ polygons: [] })}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--fg-3)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
            }}
          >
            CLR
          </button>
        )}
      </legend>

      {polygons.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {polygons.map((p) => (
            <FilterChip
              key={p.id}
              label="AOI"
              value={p.label ?? `polygon (${vertexCount(p.polygon)}pts)`}
              onRemove={() => remove(p.id)}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={openEditor}
        title="draw an area on the map"
        style={{
          marginTop: 6,
          width: '100%',
          height: 24,
          padding: '0 6px',
          background: 'var(--bg-base)',
          border: '1px dashed var(--bd-strong)',
          color: 'var(--fg-2)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        ◇ {polygons.length === 0 ? 'DRAW AOI' : 'EDIT AOI'}
      </button>
    </fieldset>
  );
}

function vertexCount(polygon: GeoPolygonEntry['polygon']): number {
  // Ring 0 is the outer ring — the only one operators usually draw. Subtract
  // the closing position (RFC 7946: first === last).
  const ring = polygon.coordinates[0];
  if (!ring) return 0;
  return Math.max(0, ring.length - 1);
}
